import { useState } from "react";
import { useNotification } from "./useNotification";
import { DeptCategoryService as CategoryService } from "@root/services/department";
import { PAGINATION_DEFAULTS, TOAST_VARIANT } from "@root/constants";

export const useDeptCategory = () => {
  const [categoriesData, setCategoriesData] = useState([]);
  const [addCategoryLoading, setAddCategoryLoading] = useState(false);
  const [updateCategoryLoading, setUpdateCategoryLoading] = useState(false);
  const [deleteCategoryLoading, setDeleteCategoryLoading] = useState(false);
  const [getCategoriesLoading, setGetCategoriesLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [page, setPage] = useState(1);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [addNewCategory, setAddNewCategory] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [pageCount, setPageCount] = useState(0);

  const categoryService = new CategoryService();
  const { showNotification } = useNotification();

  const payload = {
    limit: PAGINATION_DEFAULTS.PAGESIZE,
    offset: (page - 1) * PAGINATION_DEFAULTS.PAGESIZE,
    search: search,
  };

  const fetchCategories = async (id: number, payload: any) => {
    setGetCategoriesLoading(true);
    try {
      const res = await categoryService.fetchCategories(id, payload);
      setCategoriesData(res?.data?.data);
      setPageCount(res?.data?.data?.count);
    } catch (error: unknown) {
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: "Failed to get categories",
      });
    } finally {
      setGetCategoriesLoading(false);
    }
  };

  const updateCategory = async (
    userId: number,
    id: number,
    payload: any,
    subscriberId?: number
  ) => {
    setUpdateCategoryLoading(true);
    try {
      await categoryService.updateCategory(id, payload);
      showNotification({
        type: "success",
        message: "Category updated successfully",
      });
      setEditingId(null);
      setEditingValue("");
      fetchCategories(userId, {
        subscriberId: subscriberId,
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        offset: (page - 1) * PAGINATION_DEFAULTS.PAGESIZE,
        search: search,
      });
    } catch (err) {
      showNotification({
        type: "error",
        message: "Failed to update the category",
      });
    } finally {
      setUpdateCategoryLoading(false);
    }
  };

  const addCategory = async (
    userId: number,
    payload: any,
    subscriberId?: number
  ) => {
    setAddCategoryLoading(true);
    console.log("payload from hook", payload);
    try {
      await categoryService.addDeptCategory(payload);
      showNotification({
        type: "success",
        message: "Category added successfully",
      });
      setEditingValue("");
      setAddNewCategory(false);
      setNewCategory("");
      fetchCategories(userId, {
        subscriberId: subscriberId,
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        offset: (page - 1) * PAGINATION_DEFAULTS.PAGESIZE,
        search: search,
      });
    } catch (err) {
      showNotification({
        type: "error",
        message: "Failed to add category",
      });
    } finally {
      setAddCategoryLoading(false);
    }
  };

  const addSubCategory = async (
    userId: number,
    payload: any,
    subscriberId?: number
  ) => {
    setAddCategoryLoading(true);
    try {
      await categoryService.addSubCategory(payload);
      showNotification({
        type: "success",
        message: "Category added successfully",
      });
      setEditingValue("");
      setAddNewCategory(false);
      setNewCategory("");
      fetchCategories(userId, {
        subscriberId: subscriberId,
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        offset: (page - 1) * PAGINATION_DEFAULTS.PAGESIZE,
        search: search,
      });
    } catch (err) {
      showNotification({
        type: "error",
        message: "Failed to add category",
      });
    } finally {
      setAddCategoryLoading(false);
    }
  };

  const deleteCategory = async (
    userId: number,
    id: number,
    subscriberId?: number
  ) => {
    setDeleteCategoryLoading(true);
    try {
      await categoryService.deleteCategory(id);
      showNotification({
        type: "success",
        message: "Category deleted successfully",
      });
      fetchCategories(userId, {
        subscriberId: subscriberId,
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        offset: (page - 1) * PAGINATION_DEFAULTS.PAGESIZE,
        search: search,
      });
    } catch (err) {
      showNotification({
        type: "success",
        message: "Failed to delete category",
      });
    } finally {
      setDeleteCategoryLoading(false);
    }
  };

  return {
    addCategoryLoading,
    getCategoriesLoading,
    updateCategoryLoading,
    deleteCategoryLoading,
    addCategory,
    updateCategory,
    deleteCategory,
    fetchCategories,
    categoriesData,
    setCategoriesData,
    search,
    setSearch,
    newCategory,
    setNewCategory,
    page,
    setPage,
    hoveredId,
    setHoveredId,
    addNewCategory,
    setAddNewCategory,
    editingId,
    setEditingId,
    editingValue,
    setEditingValue,
    pageCount,
    addSubCategory,
  };
};
